import React, { useContext, useState } from "react"
import { DashboardAccountInfo } from "@ecountabl/lib"
import { ThemeContext } from "../../utility/context/ThemeContext"
import { useAppDispatch, useAppSelector } from '../../store'
import api from "../../api-client"
import { setAccounts } from "../../accounts-slice"
import { ThemedButton } from "../input/buttons"
import ThemedDialog from "../../style/dialog"
import { View, Text, TouchableOpacity, ScrollView } from "react-native"
import { FontAwesome, Ionicons } from "@expo/vector-icons"
import { AccountCard } from "../../account-card"
import { accountColorPalette } from "../../lib/account-colors"
import { textStyles } from "../../style"
import { log } from "../pwa"
import Analytics from "../analytics"

type Props = {
  account: DashboardAccountInfo
  closeSheet: () => void
}

const AccountManagement = ({ account, closeSheet }: Props): React.ReactElement => {
  const { theme } = useContext(ThemeContext)
  const { borderColor, textMain, danger, white } = theme.colors

  const { accounts } = useAppSelector((state) => state.accountsSlice)

  const dispatch = useAppDispatch()

  const [color, setColor] = useState(account.color)

  const [savingAccount, setSavingAccount] = useState(false)
  const [savingAccountError, setSavingAccountError] = useState(false)

  const saveAccount = async () => {
    Analytics.track('save_account_pressed', {
      color: color
    })
    setSavingAccount(true)
    setSavingAccountError(false)
    try {
      await api.updateAccountColors({ [account.account_id]: color })
      const newAccounts = accounts.map((acc) => {
        if (acc.account_id === account.account_id) {
          return { ...acc, color: color }
        }
        return acc
      })
      dispatch(setAccounts(newAccounts))
      closeSheet()
    } catch (error) {
      log.error(error, { tags: { message: 'error saving account' } })
      setSavingAccountError(true)
    } finally {
      setSavingAccount(false)
    }
  }

  return <>
    <View style={{flex: 1, gap: 16}}>
      <ScrollView
        style={{flex: 1}}
        contentContainerStyle={{gap: 16, paddingHorizontal: 16, justifyContent: "space-between"}}
      >
        <Text style={[textStyles.h2, {color: textMain}]}>
          Manage Account
        </Text>
        <View style={{width: "100%", alignItems: "center"}}>
          <AccountCard
            account={{
              ...account,
              color: color
            }}
          />
        </View>
        <View style={{flexDirection: "row", alignItems: "center", gap: 5}}>
          <Ionicons name="color-palette-outline" size={22} color={textMain} />
          <Text style={[textStyles.h3, {color: textMain}]}>
            Color Palette
          </Text>
        </View>
        <View style={{flexDirection: "row", gap: 16, alignItems: "center", flexWrap: "wrap", justifyContent: "center", paddingVertical: 16}}>
          {accountColorPalette.map((paletteColor) => (
            <TouchableOpacity
              key={paletteColor.value}
              onPress={() => setColor(paletteColor.value)}
            >
              <View
                style={{
                  width: 35,
                  height: 35,
                  backgroundColor: paletteColor.value,
                  borderWidth: 1,
                  borderColor: borderColor,
                  borderRadius: 100,
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                {paletteColor.value === color && <FontAwesome name="paint-brush" size={22} color={white} />}
              </View>
            </TouchableOpacity>
          ))}
        </View>
      </ScrollView>
      <View style={{width: "100%", flexDirection: "row", alignItems: "center", justifyContent: "center", paddingHorizontal: 16}}>
        <View style={{flex: 0.25, minWidth: 50}}/>
        <ThemedButton
          text="Save"
          onPress={saveAccount}
          variant="primary"
          containerStyle={{flex: 1, alignSelf: "center"}}
          loading={savingAccount}
        />
        <ThemedButton
        
        text="Reset"
        onPress={() => {
          setColor(account.color)
        }}
        variant="secondary"
        textStyle={{color: textMain}}
        containerStyle={{flex: 0.25, minWidth: 50, alignSelf: "center"}}
        style={{borderWidth: 0}}
        />
      </View>
    </View>

    {/* SAVING ACCOUNT ERROR MODAL */}
    <ThemedDialog
      title="Error Saving Account"
      subMessage="Please try again later"
      options={[
        {
          title: "Okay",
          icon: <Ionicons name="checkmark" size={22}/>,
          role: "destructive",
          onPress: () => setSavingAccountError(false)
        }
      ]}
      visible={savingAccountError}
      backDropProps={{onPress: () => setSavingAccountError(false)}}
      errorAnimation={savingAccountError}
      loading={false}
    />
  </>
}

export default AccountManagement